import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  InputLabel,
  Skeleton,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from '@mui/material';
import {
  AccessTimeRounded,
  ApartmentRounded,
  CalendarMonthRounded,
  CheckCircleRounded,
  Close as CloseIcon,
  Delete as DeleteIcon,
  EventAvailable as EventAvailableIcon,
  GroupsRounded,
  HelpOutlineRounded,
  PersonOutlineRounded,
  PointOfSaleRounded,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import { esES } from '@mui/x-date-pickers/locales';
import AfiliadoSelect from '../Afiliados/AfiliadoSelect';
import { useAfiliados } from '../Afiliados/AfiliadosProvider';
import ButtonBack from '../Breadcrumb/ButtonBack';
import CustomizedSnackbars from '../Notifications/SnackBar';
import LoadingButton from '../../@iosper/components/LoadingButton';
import {
  agendarTurno,
  buscarAgenciasHabilitadas,
  buscarFranjasDisponibles,
  cancelarTurno,
  misTurnosAgendados,
} from '../../services/Turnero/Turnero';

/**
 * Turno Previo (ticket #25, ver PLAN_TURNERO_PREVIO.md): el afiliado elige
 * a quién del grupo familiar, en qué sede y para qué día/horario
 * agendar un turno. La categoría de trámite NO se pide acá: se elige recién
 * en la Terminal de autogestión al hacer check-in, el día de la cita.
 * También puede ver y cancelar sus reservas activas (propias o de un
 * familiar) mientras no hayan hecho check-in todavía.
 *
 * Nota sobre manejo de errores: no se muestra ningún mensaje propio en los
 * catch de las llamadas HTTP -- el interceptor global de axios
 * (services/Axios/store/InterceptorProvider.js) ya le muestra al usuario un
 * snackbar apropiado para cualquier request que falle (el mensaje de
 * negocio para 400/401/422, uno generico para 500 sin exponer el detalle
 * interno). Agregar un mensaje propio acá solo duplicaba ese aviso y, para
 * errores 500 inesperados, terminaba mostrando el texto crudo de la
 * excepcion del backend en vez del genérico seguro que ya muestra el
 * interceptor.
 *
 * Nota sobre la vista (mockup 2026-09): stepper de progreso SOLO en desktop
 * (el form es de una sola página, no un wizard); en mobile el detalle
 * "¿Qué pasa después?" se muestra en un diálogo desde el ícono de ayuda.
 * El chip usa `turno.estadoDescripcion` (`mis-agendados` sólo devuelve
 * turnos AGENDADO, o sea siempre "Pendiente de confirmación");
 * el texto fijo es sólo fallback si el backend no lo manda. Ver
 * WORKFLOW_TURNERO_PREVIO_UI.md para los cambios de API pendientes
 * (idAfiliado, endpoint de historial "Ver todos mis turnos").
 */

const PASOS = ['Afiliado', 'Sede y fecha', 'Horario', 'Confirmación'];

// Función (no array fijo) porque la tolerancia del check-in viene del backend
// (TurnoAction::MINUTOS_TOLERANCIA_CHECKIN) y no debe quedar hardcodeada acá:
// se pasa en la pantalla de confirmación, y en los demás usos (antes de
// agendar, ayuda mobile) se usa el valor por defecto.
const MINUTOS_TOLERANCIA_DEFAULT = 30;

const pasosDespues = (minutosTolerancia = MINUTOS_TOLERANCIA_DEFAULT) => [
  {
    icon: ApartmentRounded,
    titulo: 'Llegá a la sede',
    texto: 'Acercate a la sede seleccionada el día y horario de tu turno.',
  },
  {
    icon: PointOfSaleRounded,
    titulo: 'Confirmá tu llegada',
    texto: `Registrá tu llegada en la terminal de autogestión desde ${minutosTolerancia} minutos antes hasta ${minutosTolerancia} minutos después del horario reservado.`,
  },
  {
    icon: GroupsRounded,
    titulo: 'Esperá tu llamado',
    texto: 'Seleccioná en la terminal de autogestión el trámite que vas a realizar.',
  },
];

// El ícono se alinea con el control de carga (abajo), no con el label: los
// tres campos tienen su label/placeholder arriba y el input abajo, así que
// `flex-end` deja el ícono a la altura del input en los tres.
// En mobile el ícono es más chico y queda más pegado al campo para que el
// texto (afiliado, sede) no se corte contra la flecha del desplegable.
const CampoConIcono = ({ icon: Icon, children }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: { xs: 1, md: 2 } }}>
    <Avatar
      variant='rounded'
      sx={{
        bgcolor: 'action.hover',
        color: 'secondary.main',
        width: { xs: 28, md: 36 },
        height: { xs: 28, md: 36 },
        flexShrink: 0,
        mb: '2px',
      }}
    >
      <Icon fontSize='small' />
    </Avatar>
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>{children}</Box>
  </Box>
);

const etiquetaAgencia = (agencia) =>
  [agencia.descripcion, agencia.localidad, agencia.direccion].filter(Boolean).join(' · ');

// Fila de la confirmación en mobile: ícono + dato principal (+ detalle chico).
const ItemReserva = ({ icon: Icon, texto, detalle }) => (
  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 0.75 }}>
    <Icon fontSize='small' sx={{ mt: '2px' }} />
    <Box sx={{ minWidth: 0 }}>
      <Typography variant='body2' sx={{ fontWeight: 600, color: 'text.primary' }}>
        {texto}
      </Typography>
      {detalle && (
        <Typography variant='caption' color='text.secondary' component='p'>
          {detalle}
        </Typography>
      )}
    </Box>
  </Box>
);

const QuePasaDespues = ({ minutosTolerancia } = {}) => (
  <Grid container spacing={2}>
    {pasosDespues(minutosTolerancia).map(({ icon: Icon, titulo, texto }, index) => (
      <Grid item xs={12} sm={4} key={titulo}>
        <Box sx={{ display: 'flex', gap: 1.5 }}>
          <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32, fontSize: 14 }}>
            {index + 1}
          </Avatar>
          <Box>
            <Typography variant='subtitle2' sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Icon fontSize='small' color='action' />
              {titulo}
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {texto}
            </Typography>
          </Box>
        </Box>
      </Grid>
    ))}
  </Grid>
);

export const TurnoPrevioAcceso = () => {
  const theme = useTheme();
  const esMobile = !useMediaQuery(theme.breakpoints.up('md'));

  const { afiliados } = useAfiliados();

  const [afiliadoSelected, setAfiliadoSelected] = useState(null);
  // Fuerza el remount de AfiliadoSelect al volver de una confirmación (ver
  // handleVolver): AfiliadoSelect maneja su propia selección internamente
  // (no es controlado), así que limpiar afiliadoSelected acá no alcanza
  // para que su UI deje de mostrar el afiliado recién agendado.
  const [formResetKey, setFormResetKey] = useState(0);

  const [agencias, setAgencias] = useState([]);
  const [cargandoAgencias, setCargandoAgencias] = useState(true);
  const [idAgencia, setIdAgencia] = useState('');

  const agenciaSeleccionada = useMemo(
    () => agencias.find((a) => a.idAgencia === Number(idAgencia)) || null,
    [agencias, idAgencia],
  );

  const [fecha, setFecha] = useState(null);
  const [franjas, setFranjas] = useState([]);
  const [cargandoFranjas, setCargandoFranjas] = useState(false);
  const [slotSeleccionado, setSlotSeleccionado] = useState(null);

  const [confirmando, setConfirmando] = useState(false);
  const [reservaConfirmada, setReservaConfirmada] = useState(null);

  const [misTurnos, setMisTurnos] = useState([]);
  const [cargandoMisTurnos, setCargandoMisTurnos] = useState(true);
  const [cancelandoId, setCancelandoId] = useState(null);
  const [mensajeExito, setMensajeExito] = useState('');

  const [ayudaAbierta, setAyudaAbierta] = useState(false);

  // En mobile la fila de "Mis turnos previos" trunca el texto (noWrap) y no
  // siempre se alcanza a leer la agencia -- al tocar la fila se abre este
  // detalle con toda la info del turno.
  const [turnoDetalleAbierto, setTurnoDetalleAbierto] = useState(null);

  // En mobile, al elegir un horario se hace scroll hasta el botón "Reservar
  // turno" para que el afiliado vea cuál es el paso siguiente. No puede ser
  // sticky: la barra de navegación inferior (fija, MobileBottomBar) y el
  // botón flotante del QR lo taparían.
  const botonReservarRef = useRef(null);

  const cargarFranjas = useCallback(async () => {
    if (!idAgencia || !fecha) {
      setFranjas([]);
      return;
    }
    try {
      setCargandoFranjas(true);
      const result = await buscarFranjasDisponibles(idAgencia, fecha.format('YYYY-MM-DD'));
      setFranjas(result || []);
    } catch (err) {
      // El interceptor global ya le avisa al usuario que la request fallo.
    } finally {
      setCargandoFranjas(false);
    }
  }, [idAgencia, fecha]);

  const cargarMisTurnos = useCallback(async () => {
    try {
      setCargandoMisTurnos(true);
      const result = await misTurnosAgendados();
      setMisTurnos(result || []);
    } catch (err) {
      // El interceptor global ya le avisa al usuario que la request fallo.
    } finally {
      setCargandoMisTurnos(false);
    }
  }, []);

  useEffect(() => {
    const cargarAgencias = async () => {
      try {
        setCargandoAgencias(true);
        const result = await buscarAgenciasHabilitadas();
        setAgencias(result || []);
      } catch (err) {
        // El interceptor global ya le avisa al usuario que la request fallo.
      } finally {
        setCargandoAgencias(false);
      }
    };

    cargarAgencias();
    cargarMisTurnos();
  }, [cargarMisTurnos]);

  useEffect(() => {
    setSlotSeleccionado(null);
    cargarFranjas();
  }, [cargarFranjas]);

  // Paso activo del stepper (solo indicador de progreso, el form es de una
  // sola página). En la pantalla de confirmación se marca todo completo.
  const activeStep = useMemo(() => {
    if (reservaConfirmada) return PASOS.length;
    if (afiliadoSelected && idAgencia && fecha && slotSeleccionado) return 3;
    if (afiliadoSelected && idAgencia && fecha) return 2;
    if (afiliadoSelected) return 1;
    return 0;
  }, [afiliadoSelected, idAgencia, fecha, slotSeleccionado, reservaConfirmada]);

  // El backend (TurnoRegistro::agendar()) rechaza una segunda reserva
  // AGENDADO para el mismo afiliado -- cualquier sede, cualquier trámite --
  // mientras la anterior siga vigente. Se anticipa ese rechazo acá cruzando
  // `misTurnos` por `idAfiliado` (campo que devuelve `mis-agendados`): si el
  // afiliado elegido ya tiene uno, se avisa y se bloquea "Reservar turno".
  // Se recalcula al cambiar de afiliado (dependencia de este useMemo).
  const turnoExistenteDelAfiliado = useMemo(() => {
    const idAfiliadoSel = afiliadoSelected?.afiliado?.idAfiliado;
    if (!idAfiliadoSel) return null;
    return (
      misTurnos.find((t) => t.idAfiliado != null && Number(t.idAfiliado) === Number(idAfiliadoSel)) ||
      null
    );
  }, [misTurnos, afiliadoSelected]);

  const handleAgendar = async () => {
    if (!afiliadoSelected || !slotSeleccionado) {
      return;
    }

    try {
      setConfirmando(true);
      // El ticket/codigoMostrar todavia no existe: se asigna recien en el
      // check-in en la Terminal (ver TurnoRegistro::agendar()), no aca.
      const turno = await agendarTurno({
        idAgencia,
        fechaHoraAgendada: slotSeleccionado.fechaHoraInicio,
        idAfiliado: afiliadoSelected?.afiliado?.idAfiliado,
      });
      setReservaConfirmada({
        agenciaDescripcion: agenciaSeleccionada?.descripcion,
        agenciaLocalidad: agenciaSeleccionada?.localidad,
        agenciaDireccion: agenciaSeleccionada?.direccion,
        fechaHoraInicio: slotSeleccionado.fechaHoraInicio,
        afiliadoNombre: [afiliadoSelected?.personaFisica?.nombre, afiliadoSelected?.personaFisica?.apellido]
          .filter(Boolean)
          .join(' '),
        // Se toma de la respuesta (TurnoAction::MINUTOS_TOLERANCIA_CHECKIN),
        // nunca hardcodeado, para que este texto no quede desincronizado.
        minutosTolerancia: turno?.minutosToleranciaCheckin,
      });
      setSlotSeleccionado(null);
      await Promise.all([cargarMisTurnos(), cargarFranjas()]);
    } catch (err) {
      // El interceptor global ya le avisa al usuario que la request fallo
      // (mensaje de negocio si es 400/401/422, generico si es 500).
    } finally {
      setConfirmando(false);
    }
  };

  const handleCancelar = async (idTurno) => {
    try {
      setCancelandoId(idTurno);
      await cancelarTurno(idTurno);
      await Promise.all([cargarMisTurnos(), cargarFranjas()]);
      setMensajeExito('El turno fue cancelado correctamente');
    } catch (err) {
      // El interceptor global ya le avisa al usuario que la request fallo.
    } finally {
      setCancelandoId(null);
    }
  };

  const handleBorrarMensajeExito = () => setMensajeExito('');

  // Al volver de la confirmación no debe quedar precargado el mismo
  // afiliado/sede/fecha del turno recién agendado -- se limpia el form
  // entero para que el afiliado elija de nuevo si quiere sacar otro turno.
  const handleVolver = () => {
    setReservaConfirmada(null);
    setAfiliadoSelected(null);
    setIdAgencia('');
    setFecha(null);
    setSlotSeleccionado(null);
    setFormResetKey((k) => k + 1);
  };

  const seccionMisTurnos = (
    <Card sx={{ p: 1, height: '100%' }}>
      <CardContent sx={{ '&:last-child': { pb: 1 } }}>
        <Typography
          gutterBottom
          variant='subtitle1'
          component='h2'
          sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
        >
          <CalendarMonthRounded fontSize='small' color='secondary' />
          Mis turnos previos
        </Typography>
        {cargandoMisTurnos ? (
          <Skeleton variant='rounded' width='100%' sx={{ height: '36px' }} />
        ) : misTurnos.length === 0 ? (
          <Typography variant='body2' color='textSecondary'>
            No tenés turnos previos reservados.
          </Typography>
        ) : (
          <Box>
            {misTurnos.map((turno, index) => {
              // agenciaDescripcion viene del turno, pero localidad/dirección
              // no -- se cruzan con `agencias` (ya cargado para el selector
              // de sede), que trae esos datos para toda agencia con turnero
              // habilitado, es decir, cualquier agencia donde pueda existir
              // un turno previo.
              const agenciaTurno = agencias.find((a) => a.idAgencia === Number(turno.idAgencia));
              const localidadDireccion = [agenciaTurno?.localidad, agenciaTurno?.direccion]
                .filter(Boolean)
                .join(' · ');

              return (
                <Box key={turno.idTurno}>
                  {index > 0 && <Divider />}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      flexWrap: 'nowrap',
                      py: 1,
                      cursor: { xs: 'pointer', sm: 'default' },
                    }}
                    onClick={
                      esMobile
                        ? () => setTurnoDetalleAbierto({ turno, agenciaTurno, localidadDireccion })
                        : undefined
                    }
                  >
                    <Typography variant='body2' color='text.secondary' noWrap sx={{ flexGrow: 1, minWidth: 0 }}>
                      <Box component='span' sx={{ fontWeight: 600, color: 'text.primary' }}>
                        {turno.afiliadoNombre || 'Afiliado'}
                      </Box>
                      {' · '}
                      {dayjs(turno.fechaHoraAgendada).format('DD/MM HH:mm')} hs · {turno.agenciaDescripcion}
                      {localidadDireccion && ` · ${localidadDireccion}`}
                    </Typography>
                    <Chip
                      size='small'
                      icon={<AccessTimeRounded sx={{ fontSize: 14 }} />}
                      label={turno.estadoDescripcion || 'Pendiente de confirmación'}
                      sx={{
                        flexShrink: 0,
                        // En mobile no entra junto al texto: se omite (todos los
                        // de esta lista están "pendientes de confirmación" igual).
                        display: { xs: 'none', sm: 'inline-flex' },
                        bgcolor: '#FFF3E0',
                        color: '#E65100',
                        fontWeight: 500,
                        '& .MuiChip-icon': { color: '#E65100' },
                      }}
                    />
                    {turno.cancelable !== false && (
                      <Tooltip title='Cancelar turno'>
                        <span>
                          <IconButton
                            edge='end'
                            size='small'
                            color='error'
                            sx={{ flexShrink: 0 }}
                            disabled={cancelandoId === turno.idTurno}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelar(turno.idTurno);
                            }}
                          >
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </span>
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </CardContent>
    </Card>
  );

  if (reservaConfirmada) {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <ButtonBack />
          {!esMobile && (
            <Box sx={{ mb: 2 }}>
              <Stepper activeStep={activeStep}>
                {PASOS.map((label) => (
                  <Step key={label}>
                    <StepLabel>{label}</StepLabel>
                  </Step>
                ))}
              </Stepper>
            </Box>
          )}
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Typography gutterBottom variant='h5' component='h2'>
                Turno reservado
              </Typography>
              <Alert
                severity='success'
                sx={{
                  mt: 2,
                  // Mobile: menos margen lateral (padding del cartel y espacio
                  // tras el ícono de éxito) para que los items tengan más lugar.
                  px: { xs: 1, md: 2 },
                  '& .MuiAlert-icon': { mr: { xs: 1, md: 1.5 } },
                }}
              >
                {esMobile ? (
                  // Mobile: el párrafo largo se reduce a items (afiliado, fecha, sede).
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Typography variant='body1' sx={{ fontWeight: 600 }}>
                      Turno reservado
                    </Typography>
                    <ItemReserva icon={PersonOutlineRounded} texto={reservaConfirmada.afiliadoNombre} />
                    <ItemReserva
                      icon={CalendarMonthRounded}
                      texto={dayjs(reservaConfirmada.fechaHoraInicio).format('DD/MM/YYYY · HH:mm [hs]')}
                    />
                    <ItemReserva
                      icon={ApartmentRounded}
                      texto={reservaConfirmada.agenciaDescripcion}
                      detalle={[reservaConfirmada.agenciaLocalidad, reservaConfirmada.agenciaDireccion]
                        .filter(Boolean)
                        .join(' · ')}
                    />
                    <Typography variant='body2'>
                      Si necesitás cancelarlo, hacelo desde “Mis turnos previos”, mientras no lo
                      hayas confirmado en la sede.
                    </Typography>
                  </Box>
                ) : (
                  <>
                    <Typography variant='body1'>
                      El turno para <b>{reservaConfirmada.afiliadoNombre}</b> quedó reservado para el{' '}
                      <b>{dayjs(reservaConfirmada.fechaHoraInicio).format('DD/MM/YYYY [a las] HH:mm')}hs</b> en{' '}
                      <b>{reservaConfirmada.agenciaDescripcion}</b>. Si necesitás cancelarlo, hacelo
                      desde “Mis turnos previos”, mientras no lo hayas confirmado en la sede.
                    </Typography>
                    {(reservaConfirmada.agenciaLocalidad || reservaConfirmada.agenciaDireccion) && (
                      <Typography variant='body2' sx={{ mt: 0.5 }}>
                        {[reservaConfirmada.agenciaLocalidad, reservaConfirmada.agenciaDireccion]
                          .filter(Boolean)
                          .join(' · ')}
                      </Typography>
                    )}
                  </>
                )}
              </Alert>

              <Box sx={{ mt: 3 }}>
                <Typography variant='subtitle1' component='h3' gutterBottom>
                  ¿Qué pasa después?
                </Typography>
                <QuePasaDespues minutosTolerancia={reservaConfirmada.minutosTolerancia} />
              </Box>
              <Button sx={{ mt: 3 }} variant='outlined' onClick={handleVolver}>
                Volver
              </Button>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    );
  }

  const botonReservar = (
    <LoadingButton
      variant='contained'
      color={slotSeleccionado ? 'success' : 'primary'}
      startIcon={<EventAvailableIcon />}
      fullWidth={esMobile}
      loading={confirmando ? 'show' : 'hide'}
      disabled={
        !afiliadoSelected || !slotSeleccionado || confirmando || !!turnoExistenteDelAfiliado
      }
      onClick={handleAgendar}
      content={
        slotSeleccionado
          ? `Reservar turno · ${dayjs(slotSeleccionado.fechaHoraInicio).format('HH:mm')} hs`
          : 'Reservar turno'
      }
    />
  );

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <ButtonBack />
          {esMobile && (
            <Tooltip title='¿Qué pasa después?'>
              <IconButton color='secondary' onClick={() => setAyudaAbierta(true)}>
                <HelpOutlineRounded />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Grid>

      {!esMobile && (
        <Grid item xs={12}>
          <Stepper activeStep={activeStep}>
            {PASOS.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Grid>
      )}

      {/* En mobile no se muestra la sección si no hay turnos previos (ni
          mientras carga, para que no aparezca y desaparezca). */}
      {(!esMobile || misTurnos.length > 0) && (
        <Grid item xs={12}>
          {seccionMisTurnos}
        </Grid>
      )}

      <Grid item xs={12}>
        <Card sx={{ p: 1 }}>
          <CardContent sx={{ pb: { xs: 0, sm: 2 } }}>
            <Typography gutterBottom variant='h5' component='h2'>
              Turno atención en Sede
            </Typography>
            <Alert severity='info' sx={{ mt: 2, mb: { xs: 1.5, sm: 0 } }}>
              <Typography variant='body2' color='textSecondary' component='p'>
                Elegí para quién es el turno, la sede, el día y el horario. Recordá solicitarlo con
                al menos 2 horas de anticipación.
              </Typography>
            </Alert>
          </CardContent>

          <CardContent sx={{ pt: { xs: 0, sm: 2 } }}>
            <Grid container rowSpacing={3} columnSpacing={3}>
              <Grid item xs={12} md={4}>
                <CampoConIcono icon={PersonOutlineRounded}>
                  {/* AfiliadoSelect trae su propio InputLabel con un offset
                      pensado para otras pantallas; acá se anula para que el
                      título quede alineado con el Select, como en los otros
                      dos campos. */}
                  <Box key={formResetKey} sx={{ '& .MuiInputLabel-root': { marginLeft: 0 } }}>
                    <AfiliadoSelect
                      onDefaultAfiliado={afiliados?.[0]?.afiliado?.idAfiliado}
                      onSelectAfiliado={(e) => setAfiliadoSelected(e)}
                    />
                  </Box>
                </CampoConIcono>
              </Grid>

              {/* Columna más ancha que las otras dos (y "fecha" más angosta):
                  la sede necesita lugar para mostrar localidad y dirección,
                  no sólo el nombre. */}
              <Grid item xs={12} md={5}>
                <CampoConIcono icon={ApartmentRounded}>
                  {/* Autocomplete (igual que el selector de prácticas de
                      Emitir Orden / Consultar Cupo) con el label en bloque
                      arriba, como AfiliadoSelect, para que los tres inputs
                      queden a la misma altura y el ícono se alinee con el
                      control. Se puede tipear para filtrar por sede,
                      localidad o dirección. */}
                  <Box sx={{ width: '100%' }}>
                    <InputLabel htmlFor='agencia-autocomplete'>Seleccioná la sede</InputLabel>
                    {cargandoAgencias ? (
                      <Skeleton variant='rounded' width='100%' sx={{ height: '32px' }} />
                    ) : (
                      <Autocomplete
                        id='agencia-autocomplete'
                        fullWidth
                        options={agencias}
                        value={agenciaSeleccionada}
                        onChange={(event, nuevaAgencia) =>
                          setIdAgencia(nuevaAgencia ? nuevaAgencia.idAgencia : '')
                        }
                        isOptionEqualToValue={(option, value) => option.idAgencia === value.idAgencia}
                        getOptionLabel={etiquetaAgencia}
                        noOptionsText='No hay sedes que coincidan'
                        renderInput={(params) => (
                          <TextField {...params} variant='standard' placeholder='Elegí una sede' />
                        )}
                      />
                    )}
                  </Box>
                </CampoConIcono>
              </Grid>

              <Grid item xs={12} md={3}>
                <CampoConIcono icon={CalendarMonthRounded}>
                  <Box sx={{ width: '100%' }}>
                    <InputLabel>Seleccioná la fecha</InputLabel>
                    <LocalizationProvider
                      dateAdapter={AdapterDayjs}
                      adapterLocale='es'
                      localeText={esES.components.MuiLocalizationProvider.defaultProps.localeText}
                    >
                      <DatePicker
                        value={fecha}
                        onChange={(newValue) => setFecha(newValue)}
                        disablePast
                        // Sólo se atiende de lunes a viernes (dayjs: 0 = domingo, 6 = sábado).
                        shouldDisableDate={(dia) => dia.day() === 0 || dia.day() === 6}
                        maxDate={dayjs().add(60, 'day')}
                        disabled={!idAgencia}
                        slotProps={{ textField: { fullWidth: true, variant: 'standard' } }}
                      />
                    </LocalizationProvider>
                  </Box>
                </CampoConIcono>
              </Grid>
            </Grid>

            {idAgencia && fecha && (
              <Box sx={{ mt: 3 }}>
                <Typography
                  variant='subtitle2'
                  gutterBottom
                  sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}
                >
                  <AccessTimeRounded fontSize='small' color='secondary' />
                  Horarios disponibles
                </Typography>
                {cargandoFranjas ? (
                  <Skeleton variant='rounded' width='100%' sx={{ height: '64px' }} />
                ) : franjas.length === 0 ? (
                  <Alert severity='warning'>No hay horarios disponibles para esa fecha.</Alert>
                ) : (
                  <Grid container spacing={1}>
                    {franjas.map((franja) => {
                      const sinCupo = franja.cupoDisponible <= 0;
                      const seleccionado =
                        slotSeleccionado?.fechaHoraInicio === franja.fechaHoraInicio;
                      return (
                        <Grid item xs={3} sm={4} md={3} lg={2} key={franja.fechaHoraInicio}>
                          <Button
                            fullWidth
                            disableElevation
                            variant={seleccionado ? 'contained' : 'outlined'}
                            color='success'
                            disabled={sinCupo}
                            onClick={() => {
                              setSlotSeleccionado(franja);
                              if (esMobile) {
                                botonReservarRef.current?.scrollIntoView({
                                  behavior: 'smooth',
                                  block: 'center',
                                });
                              }
                            }}
                            sx={{
                              flexDirection: 'column',
                              gap: 0.25,
                              py: 1,
                              // Alto fijo para que la fila no "salte" al elegir un
                              // horario (aparece el tilde) o si alguno no tiene cupo.
                              minHeight: 56,
                              textTransform: 'none',
                              lineHeight: 1.2,
                            }}
                          >
                            <Typography
                              component='span'
                              sx={{
                                fontWeight: 700,
                                fontSize: '1.15rem',
                                lineHeight: 1,
                                color: sinCupo ? 'text.disabled' : 'inherit',
                              }}
                            >
                              {dayjs(franja.fechaHoraInicio).format('HH:mm')}
                            </Typography>
                            {/* Sólo se aclara el caso especial: un horario habilitado
                                ya se entiende disponible sin decirlo. "Sin cupo" en una
                                sola línea (nowrap) y con color apagado, para que se note
                                de un vistazo que el horario no se puede tocar. */}
                            {seleccionado ? (
                              <CheckCircleRounded sx={{ fontSize: 14 }} />
                            ) : (
                              sinCupo && (
                                <Box
                                  component='span'
                                  sx={{ fontSize: 11, whiteSpace: 'nowrap', color: 'text.disabled' }}
                                >
                                  Sin cupo
                                </Box>
                              )
                            )}
                          </Button>
                        </Grid>
                      );
                    })}
                  </Grid>
                )}
              </Box>
            )}

            {turnoExistenteDelAfiliado && (
              <Alert severity='warning' sx={{ mt: 3 }}>
                <Typography variant='body2'>
                  {turnoExistenteDelAfiliado.afiliadoNombre || 'Este afiliado'} ya tiene un turno
                  agendado pendiente de registrar su llegada
                  {turnoExistenteDelAfiliado.fechaHoraAgendada
                    ? `: ${dayjs(turnoExistenteDelAfiliado.fechaHoraAgendada).format('DD/MM [a las] HH:mm')} h${
                        turnoExistenteDelAfiliado.agenciaDescripcion
                          ? ` – ${turnoExistenteDelAfiliado.agenciaDescripcion}`
                          : ''
                      }`
                    : ''}
                  . Para sacar otro turno, primero deberá registrar su llegada en la sede el día de
                  la cita o cancelar el turno actual desde “Mis turnos previos”.
                </Typography>
              </Alert>
            )}
          </CardContent>

          <CardContent sx={{ pt: 0 }}>
            <Box
              ref={botonReservarRef}
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: { xs: 'stretch', md: 'flex-end' },
              }}
            >
              {botonReservar}
              <Typography
                variant='caption'
                color='text.secondary'
                align={esMobile ? 'center' : 'right'}
                sx={{ mt: 0.5 }}
              >
                Al reservar, verás en pantalla los datos de la cita.
              </Typography>
            </Box>
          </CardContent>
        </Card>
      </Grid>

      {!esMobile && (
        <Grid item xs={12}>
          <Card sx={{ p: 1 }}>
            <CardContent>
              <Typography variant='subtitle1' component='h2' gutterBottom>
                ¿Qué pasa después?
              </Typography>
              <QuePasaDespues />
            </CardContent>
          </Card>
        </Grid>
      )}

      <Dialog open={ayudaAbierta} onClose={() => setAyudaAbierta(false)} fullWidth maxWidth='xs'>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          ¿Qué pasa después?
          <IconButton onClick={() => setAyudaAbierta(false)} size='small'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <QuePasaDespues />
        </DialogContent>
      </Dialog>

      <Dialog
        open={!!turnoDetalleAbierto}
        onClose={() => setTurnoDetalleAbierto(null)}
        fullWidth
        maxWidth='xs'
      >
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Detalle del turno
          <IconButton onClick={() => setTurnoDetalleAbierto(null)} size='small'>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {turnoDetalleAbierto && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, pb: 1 }}>
              <ItemReserva
                icon={PersonOutlineRounded}
                texto={turnoDetalleAbierto.turno.afiliadoNombre || 'Afiliado'}
              />
              <ItemReserva
                icon={CalendarMonthRounded}
                texto={dayjs(turnoDetalleAbierto.turno.fechaHoraAgendada).format('DD/MM/YYYY · HH:mm [hs]')}
              />
              <ItemReserva
                icon={ApartmentRounded}
                texto={turnoDetalleAbierto.turno.agenciaDescripcion}
                detalle={turnoDetalleAbierto.localidadDireccion}
              />
            </Box>
          )}
        </DialogContent>
      </Dialog>

      <CustomizedSnackbars
        open={mensajeExito}
        autoHideDuration={8000}
        severity='success'
        message={mensajeExito}
        onDeleteMessage={handleBorrarMensajeExito}
      />
    </Grid>
  );
};
